import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Spin } from 'antd';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { isEmpty, isNil } from 'ramda';
import { parse } from 'qs';
import Helmet from 'react-helmet';
import { throttle } from 'lodash';

import { autocomplete, clearAutocomplete } from 'actions/search';
import analytics from 'helpers/analytics';
import { smoothScroll } from 'helpers/view';
import {
  foundReleaseList,
  foundTrackList,
  foundTotal,
  foundArtistList,
  foundGenreList,
  foundSeriesList,
} from 'selectors/search';
import SearchByType from './SearchByType';
import LoadingContainer from '../../components/LoadingContainer';
import ArtistBlock from './ArtistBlock/ArtistsBlock';
import { ReleaseBlock } from './ReleaseBlock/ReleaseBlock';
import SeriesBlock from './SeriesBlock/SeriesBlock';
import GenresBlock from './GenresBlock/GenresBlock';
import TracksBlock from './TracksBlock/TracksBlock';

import c from './Search.less';

const perPage = 6;
const perPageByType = 20;

const getFpn = (type) => {
  let fpn = '';

  switch (type) {
    case 'releases':
      fpn = '/catalog/search/release';
      break;
    case 'tracks':
      fpn = '/catalog/search/song';
      break;
    case 'artists':
      fpn = '/catalog/search/artist';
      break;
    default:
      fpn = '/catalog/search';
  }

  return fpn;
};

class SearchContainer extends React.Component {
  static propTypes = {
    loading: PropTypes.bool.isRequired,
    resultList: PropTypes.object,
    location: PropTypes.object.isRequired,
    autocomplete: PropTypes.func.isRequired,
    clearAutocomplete: PropTypes.func.isRequired,
    trackList: PropTypes.object.isRequired,
    artistList: PropTypes.object.isRequired,
    totalResults: PropTypes.number.isRequired,
    releaseList: PropTypes.object.isRequired,
    seriesList: PropTypes.object.isRequired,
    genreList: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
  };

  static defaultProps = {
    resultList: {},
  };

  constructor(props) {
    super(props);

    this.autocomplete = throttle(this.props.autocomplete, 1000);
  }

  componentDidMount() {
    if (!isNil(this.props.resultList) && !isEmpty(this.props.resultList)) return;

    this._loadResults(this.props.location.query, this.props.match.params);

    smoothScroll();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { q, page } = nextProps.location.query;
    const { type } = nextProps.match.params;

    if (q !== this.props.location.query.q
      || type !== this.props.match.params.type
      || page !== this.props.location.query.page) {
      this._loadResults(nextProps.location.query, nextProps.match.params, q !== this.props.location.query.q);
    }

    if (
      type === this.props.match.params.type
      && page !== this.props.location.query.page
    ) {
      analytics.pageview(getFpn(type));
    }
  }

  _loadResults({ q, page }, { type }, clearBeforeLoading) {
    let pagination;

    if (type) {
      pagination = {
        offset: page ? (page - 1) * perPageByType : 0,
        limit: type ? perPageByType : perPage,
      };
    }

    if (q && q.length > 0) {
      this.autocomplete(q, type, pagination, clearBeforeLoading);
    } else {
      this.autocomplete.cancel();
      this.props.clearAutocomplete();
    }
  }

  render() {
    const {
      location,
      totalResults,
      loading,
      trackList,
      artistList,
      releaseList,
      seriesList,
      genreList,
      match,
    } = this.props;

    const { type } = match.params;

    const { q } = parse(location.search.slice(1));

    const resultsByType = {
      artists: artistList,
      releases: releaseList,
      series: seriesList,
      tracks: trackList,
      genres: genreList,
    };

    if (loading && totalResults === 0) {
      return (<LoadingContainer />);
    }

    return (
      <div className={c.container}>
        <Helmet defer={false}>
          <title>Search Music at Mp3Caprice</title>
          <meta name="description" content="Search, buy and download high quality tunes for reasonable prices." />
          <meta property="mc:dp" content={getFpn(type)} />
          <meta property="mc:page" content="Search" />
        </Helmet>
        {
          (isNil(q) || q.length === 0) ? (
            <div className={c.emptyResultsContainer}>
              <h2>NO RESULTS FOUND</h2>
              <p>Please type in at least 1 character.</p>
            </div>
          ) : null
        }
        {
          type ? (
            <SearchByType
              results={resultsByType[type]}
              type={type}
              loading={loading}
            />
          ) : (
            <Spin spinning={loading} size="large">
              {
                !isEmpty(trackList.items) ? (
                  <TracksBlock items={trackList.items} total={trackList.total} q={q} />
                ) : null
              }
              <Row gutter={20}>
                {
                  !isEmpty(artistList.items) ? (
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <ArtistBlock items={artistList.items} total={artistList.total} q={q} />
                    </Col>
                  ) : null
                }
                {
                  !isEmpty(releaseList.items) ? (
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <ReleaseBlock items={releaseList.items} total={releaseList.total} q={q} />
                    </Col>
                  ) : null
                }
                {
                  !isEmpty(seriesList.items) ? (
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <SeriesBlock items={seriesList.items} />
                    </Col>
                  ) : null
                }
                {
                  !isEmpty(genreList.items) ? (
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <GenresBlock items={genreList.items} />
                    </Col>
                  ) : null
                }
              </Row>
            </Spin>
          )
        }
        {
          !loading && totalResults === 0 && q && q.length > 0 ? (
            <div className={c.emptyResultsContainer}>
              <h2>NO RESULTS FOUND</h2>
              <p>
                No results found for <strong>“{q}”</strong>.<br />
                Send us your <Link to={'/shop/music-request'}>music request</Link> and we will try to execute it in a
                day.
              </p>
            </div>
          ) : null
        }
      </div>
    );
  }
}

const mapStateToProps = (store) => ({
  loading: store.search.fetching.loading,
  releaseList: foundReleaseList(store),
  artistList: foundArtistList(store),
  trackList: foundTrackList(store),
  seriesList: foundSeriesList(store),
  genreList: foundGenreList(store),
  totalResults: foundTotal(store),
});

const mapDispatchToProps = {
  autocomplete,
  clearAutocomplete,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SearchContainer));
